import { useEffect, useRef, type RefObject } from "react";

/**
 * 스크롤 위치에 따라 "현재 활성 섹션"을 추적하는 스크롤 스파이 훅.
 *
 * IntersectionObserver 콜백의 `entries` 는 **이번에 교차 상태가 바뀐 섹션만** 담는다
 * (현재 화면에 보이는 전체가 아니다). 그래서 콜백에 들어온 entries 만 보고 topmost 를
 * 고르면, 스크롤 도중 막 빠져나가는 직전 섹션이 잡혀 활성 목차가 한 칸씩 어긋난다.
 *
 * 이 훅은 보이는 섹션 집합(visibleSet)을 누적해 유지하고, `orderedIds`(문서 순서) 에서
 * 첫 번째 visible 을 활성으로 고른다. 보정값은 `rootMargin` 으로 호출부에서 조정한다.
 */
export interface UseScrollSpyOptions {
  /** 스크롤 컨테이너 ref (IntersectionObserver 의 root). */
  scrollRef: RefObject<HTMLElement | null>;
  /** 문서 순서대로 정렬된 섹션 id 목록. 활성 섹션은 이 순서에서 첫 visible 로 정한다. */
  orderedIds: string[];
  /** 활성 섹션이 바뀔 때 호출. */
  onActiveChange: (id: string) => void;
  /**
   * id 로 관찰 대상 엘리먼트를 찾는다. root 를 인자로 받아 컨테이너 범위 내에서 찾는다.
   * 기능마다 섹션 마킹 방식(data-section-id / data-dsd-section / id)이 달라 주입받는다.
   */
  getElement: (id: string, root: HTMLElement) => Element | null;
  /**
   * IntersectionObserver rootMargin. 활성 판정의 "보정선" 역할.
   * 기본값은 뷰포트 상단 30% 띠("0px 0px -70% 0px").
   */
  rootMargin?: string;
}

export const useScrollSpy = ({
  scrollRef,
  orderedIds,
  onActiveChange,
  getElement,
  rootMargin = "0px 0px -70% 0px",
}: UseScrollSpyOptions) => {
  // 콜백/엘리먼트 조회 함수는 렌더마다 새로 만들어질 수 있으므로 ref 로 들고
  // effect 의존성에서 제외한다(불필요한 재구독 방지).
  const onActiveChangeRef = useRef(onActiveChange);
  const getElementRef = useRef(getElement);
  const lastActiveIdRef = useRef<string | null>(null);
  onActiveChangeRef.current = onActiveChange;
  getElementRef.current = getElement;

  // orderedIds 배열은 렌더마다 새 참조일 수 있으므로 내용 기반 키로 재구독을 제어한다.
  const idsKey = orderedIds.join("\0");

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || orderedIds.length === 0) return;

    lastActiveIdRef.current = null;
    const visibleSet = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.getAttribute("data-scroll-spy-id");
          if (!id) continue;
          if (entry.isIntersecting) visibleSet.add(id);
          else visibleSet.delete(id);
        }
        const activeId = orderedIds.find((id) => visibleSet.has(id));
        if (activeId && activeId !== lastActiveIdRef.current) {
          lastActiveIdRef.current = activeId;
          onActiveChangeRef.current(activeId);
        }
      },
      { root, rootMargin, threshold: 0 },
    );

    for (const id of orderedIds) {
      const el = getElementRef.current(id, root);
      if (!el) continue;
      // 관찰 대상에 안정적인 id 를 심어, 콜백에서 어떤 섹션인지 역추적한다.
      el.setAttribute("data-scroll-spy-id", id);
      observer.observe(el);
    }

    return () => observer.disconnect();
    // idsKey 로 orderedIds 변화를 감지한다(배열 참조 대신 내용 기반).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRef, idsKey, rootMargin]);
};
